import React, { useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import logger from '../../utils/logger'
import styles from './MapboxAddressAutocomplete.module.scss'

const SUGGEST_DEBOUNCE_MS = 250
const MIN_QUERY_LENGTH = 3
const SEARCHBOX_BASE = 'https://api.mapbox.com/search/searchbox/v1'

const getMapboxToken = () => process.env.REACT_APP_MAPBOX_PUBLIC_TOKEN || ''

/**
 * Pulls an address component out of feature.properties.context.
 * Mapbox returns context as an object keyed by component type
 * (e.g. { country: { name, country_code }, region: { name, region_code }, ... }).
 */
function readContext(feature) {
  const ctx = feature && feature.properties && feature.properties.context
  if (!ctx) return {}
  return {
    streetLine:
      feature.properties.full_address?.split(',')[0]?.trim() ||
      feature.properties.address ||
      feature.properties.name ||
      '',
    city: ctx.place?.name || '',
    state:
      ctx.region?.region_code ||
      ctx.region?.region_code_full?.replace(/^US-/, '') ||
      ctx.region?.name ||
      '',
    zip: ctx.postcode?.name || '',
    country: ctx.country?.country_code || ctx.country?.name || '',
  }
}

const MapboxAddressAutocomplete = ({ value = '', onPick, onClear = null, country = 'US' }) => {
  const [query, setQuery] = useState(value || '')
  const [suggestions, setSuggestions] = useState([])
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  // Mapbox session_token is generated once per mount per their guidance.
  const sessionTokenRef = useRef(null)
  if (!sessionTokenRef.current) {
    sessionTokenRef.current =
      typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`
  }

  // Debounced fetch of /suggest
  useEffect(() => {
    const token = getMapboxToken()
    if (!token) return
    if (!query || query.trim().length < MIN_QUERY_LENGTH) {
      setSuggestions([])
      return undefined
    }

    let cancelled = false
    const timer = setTimeout(async () => {
      setLoading(true)
      setError(null)
      try {
        const url = new URL(`${SEARCHBOX_BASE}/suggest`)
        url.searchParams.set('q', query)
        url.searchParams.set('access_token', process.env.REACT_APP_MAPBOX_PUBLIC_TOKEN)
        url.searchParams.set('session_token', sessionTokenRef.current)
        url.searchParams.set('language', 'en')
        url.searchParams.set('limit', '5')
        url.searchParams.set('types', 'address')
        if (country) url.searchParams.set('country', country.toLowerCase())

        const res = await fetch(url.toString())
        if (!res.ok) throw new Error(`Mapbox suggest returned ${res.status}`)
        const body = await res.json()
        if (cancelled) return
        setSuggestions(Array.isArray(body.suggestions) ? body.suggestions : [])
        setOpen(true)
      } catch (err) {
        if (cancelled) return
        logger.warn('Mapbox suggest failed:', err.message)
        setError('Address suggestions unavailable — type the address manually.')
        setSuggestions([])
      } finally {
        if (!cancelled) setLoading(false)
      }
    }, SUGGEST_DEBOUNCE_MS)

    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [query, country])

  const handleSelect = useCallback(
    async (sugg) => {
      const token = getMapboxToken()
      if (!token) return
      setOpen(false)
      setLoading(true)
      setError(null)
      try {
        const url = new URL(`${SEARCHBOX_BASE}/retrieve/${sugg.mapbox_id}`)
        url.searchParams.set('access_token', process.env.REACT_APP_MAPBOX_PUBLIC_TOKEN)
        url.searchParams.set('session_token', sessionTokenRef.current)

        const res = await fetch(url.toString())
        if (!res.ok) throw new Error(`Mapbox retrieve returned ${res.status}`)
        const body = await res.json()
        const feature = body.features && body.features[0]
        if (!feature) throw new Error('Mapbox returned no features')

        const parsed = readContext(feature)
        const display = [parsed.streetLine, parsed.city, parsed.state, parsed.zip]
          .filter(Boolean)
          .join(', ')
        setQuery(display)
        onPick({
          ...parsed,
          mapboxPlaceId: feature.properties?.mapbox_id || sugg.mapbox_id,
          verified: true,
        })
      } catch (err) {
        logger.warn('Mapbox retrieve failed:', err.message)
        setError('Could not load that address — type it in manually.')
      } finally {
        setLoading(false)
      }
    },
    [onPick],
  )

  const handleChange = (e) => {
    const next = e.target.value
    setQuery(next)
    if (onClear) onClear()
  }

  if (!getMapboxToken()) {
    return (
      <div className={styles.disabledNote} data-testid="mapbox-disabled">
        Address autocomplete is unavailable — type your address in the fields below.
      </div>
    )
  }

  return (
    <div className={styles.wrapper} data-testid="mapbox-autocomplete">
      <label className={styles.label} htmlFor="mapbox-search">
        Search address
      </label>
      <input
        id="mapbox-search"
        type="text"
        className={styles.input}
        placeholder="Start typing your street address…"
        value={query}
        onChange={handleChange}
        onFocus={() => suggestions.length > 0 && setOpen(true)}
        autoComplete="off"
        data-testid="mapbox-input"
      />
      <p className={styles.helperText}>
        Pick a result to auto-fill the fields below, or type them in manually.
      </p>
      {loading && (
        <div className={styles.loading} data-testid="mapbox-loading">
          Searching…
        </div>
      )}
      {error && <div className={styles.error}>{error}</div>}
      {open && suggestions.length > 0 && (
        <ul className={styles.suggestionList} role="listbox" data-testid="mapbox-suggestions">
          {suggestions.map((s) => (
            <li
              key={s.mapbox_id}
              className={styles.suggestionItem}
              role="option"
              aria-selected="false"
              tabIndex="0"
              onClick={() => handleSelect(s)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSelect(s)
              }}
              data-testid={`mapbox-suggestion-${s.mapbox_id}`}
            >
              <strong>{s.name}</strong>
              <span className={styles.suggestionMeta}>
                {s.full_address || s.place_formatted || ''}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

MapboxAddressAutocomplete.propTypes = {
  value: PropTypes.string,
  onPick: PropTypes.func.isRequired,
  onClear: PropTypes.func,
  country: PropTypes.string,
}


export default MapboxAddressAutocomplete
